import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { compactoConTitulo } from '../utils/numero';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  ShieldAlert, ShieldOff, Wifi, Lock, UserCheck, RefreshCw, ExternalLink,
} from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
         LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');


const StatCard = ({ label, value, sub, color }) => (
  <div className={`stat-card stat-${color}`}>
    <div className="stat-label">{label}</div>
    <div className="stat-value">{value ?? '—'}</div>
    {sub && <div className="stat-sub">{sub}</div>}
  </div>
);

function DashboardDesktop() {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [agentReports, setAgentReports] = useState([]);
  const [anomalies, setAnomalies] = useState(null);
  const [patchSummary, setPatchSummary] = useState(null); // null = feature 'parches' off o sin datos
  const [netHealth, setNetHealth] = useState([]);          // salud de red por ubicación
  const [defender, setDefender] = useState(null);          // postura de Defender (flota)
  const [defAssets, setDefAssets] = useState([]);          // postura por equipo (peor firma)
  const [loading, setLoading] = useState(true);
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = () => {
    setLoading(true);
    Promise.all([
      api.getStats().catch(() => null),
      fetch(`${API_URL}/assets/stats/agent-reports`, { headers }).then(r => r.json()).catch(() => []),
      fetch(`${API_URL}/anomalies?resolved=false&limit=5`, { headers }).then(r => r.json()).catch(() => null),
      fetch(`${API_URL}/patches/summary`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API_URL}/network-health/dashboard`, { headers }).then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`${API_URL}/antivirus/summary`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API_URL}/antivirus/assets`, { headers }).then(r => r.ok ? r.json() : []).catch(() => []),
    ]).then(([s, ar, anom, ps, nh, def, dass]) => {
      setStats(s);
      setAgentReports(Array.isArray(ar) ? ar : []);
      setAnomalies(anom);
      setPatchSummary(ps && !ps.error ? ps : null);
      setNetHealth(Array.isArray(nh) ? nh : []);
      setDefender(def && !def.error ? def : null);
      setDefAssets(Array.isArray(dass) ? dass : []);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="loading-screen"><div className="spinner" /><span>Cargando...</span></div>;

  const total = parseInt(stats?.total || 0);
  const managedTotal = parseInt(stats?.managed_total || stats?.total || 0);
  const active = parseInt(stats?.active || 0);
  const patchPending = parseInt(stats?.warning || stats?.patch_pending || 0);
  const securityRisk = parseInt(stats?.critical || stats?.security_risk || 0);
  const noAntivirus = parseInt(stats?.no_antivirus || 0);
  const noFirewall = parseInt(stats?.no_firewall || 0);
  const noBitlocker = parseInt(stats?.no_bitlocker || 0);
  const localAdmin = parseInt(stats?.local_admin || 0);
  // Reportando = gestionables menos los que no dan señales hace más de 24 h.
  const reportando = Math.max(0, managedTotal - parseInt(stats?.stale || 0));
  // patchPct: % de equipos gestionables (con OS) que están al día
  const patchPct = managedTotal > 0 ? Math.round((active / managedTotal) * 100) : 0;


  // ── Semáforo de dominio ────────────────────────────────────────────────────
  // Un veredicto por dominio. El número es lo único grande: la etiqueta y el
  // detalle son de apoyo, para que la fila se lea de un vistazo y no se lea "toda".
  const exclSospechosas = Number(defender?.exclusiones?.sospechosas || 0);
  const firmasViejas    = Number(defender?.firmas_viejas || 0);
  const defenderOff     = Number(defender?.rtp_apagada || 0);
  const segCriticos     = exclSospechosas + defenderOff + noAntivirus;
  const parchesFuera    = patchPending + (patchSummary ? 0 : 0);
  const parchesCriticos = Number(patchSummary?.critical || 0);
  const sedesDegradadas = netHealth.filter(n => n.estado === 'degradado').length;

  const nivel = (crit, warn) => (crit > 0 ? 'c' : warn > 0 ? 'w' : 'o');

  const dominios = [
    {
      key: 'seg', label: 'Seguridad', to: '/security',
      valor: segCriticos || firmasViejas,
      sub: segCriticos > 0
        ? `equipo${segCriticos === 1 ? '' : 's'} con hallazgos críticos`
        : firmasViejas > 0 ? `equipo${firmasViejas === 1 ? '' : 's'} con firmas vencidas` : 'sin hallazgos',
      detalle: [
        exclSospechosas > 0 && `${exclSospechosas} con exclusiones sospechosas`,
        firmasViejas > 0 && `${firmasViejas} con firmas vencidas`,
        defenderOff > 0 && `${defenderOff} sin Defender`,
      ].filter(Boolean).join(' · ') || 'antivirus, firewall y cifrado en orden',
      nivel: nivel(segCriticos, firmasViejas),
    },
    {
      key: 'par', label: 'Parches', to: patchSummary ? '/parches' : '/assets',
      valor: parchesFuera,
      sub: `equipo${parchesFuera === 1 ? '' : 's'} fuera de fecha`,
      detalle: [
        parchesCriticos > 0 && `${parchesCriticos} parches críticos`,
        patchSummary?.pending_reboot > 0 && `${patchSummary.pending_reboot} esperan reinicio`,
        `${patchPct}% de la flota al día`,
      ].filter(Boolean).join(' · '),
      nivel: nivel(parchesCriticos, parchesFuera),
    },
    {
      key: 'red', label: 'Red', to: '/network-health',
      valor: sedesDegradadas,
      sub: netHealth.length
        ? `sede${sedesDegradadas === 1 ? '' : 's'} degradada${sedesDegradadas === 1 ? '' : 's'} de ${netHealth.length}`
        : 'sin redes habilitadas',
      detalle: netHealth.find(n => n.estado === 'degradado')
        ? `${netHealth.find(n => n.estado === 'degradado').name} · ${netHealth.find(n => n.estado === 'degradado').motivos[0]}`
        : netHealth.length ? 'todas las sedes dentro de parámetro' : 'cargá las subredes de tus sedes',
      nivel: nivel(0, sedesDegradadas),
    },
  ];

  // ── Titulares ──────────────────────────────────────────────────────────────
  // Lo accionable, con nombre y apellido. Cada uno trae su propia unidad —
  // equipos, parches, días, porcentaje — porque comparar magnitudes distintas
  // con la misma escala seria mentir.
  const peorFirma = defAssets
    .filter(a => a.defender_signature_age_days != null)
    .sort((a, b) => b.defender_signature_age_days - a.defender_signature_age_days)[0];

  const titulares = [
    parchesCriticos > 0 && {
      n: parchesCriticos, unidad: '', nivel: 'c', to: '/parches',
      titulo: 'Parches críticos sin aplicar',
      detalle: patchSummary?.pending_reboot > 0
        ? `${patchSummary.pending_reboot} equipos esperando reinicio`
        : 'sin ventana programada',
      meta: 'exposición activa',
    },
    peorFirma?.defender_signature_age_days > 7 && {
      n: peorFirma.defender_signature_age_days, unidad: 'd', nivel: 'w', to: '/antivirus',
      titulo: 'Días sin actualizar firmas de antivirus',
      detalle: peorFirma.hostname + (firmasViejas > 1 ? ` · y ${firmasViejas - 1} equipo${firmasViejas > 2 ? 's' : ''} más` : ''),
      meta: 'antivirus ciego',
    },
    ...netHealth.filter(n => n.estado === 'degradado').slice(0, 2).map(n => ({
      n: n.motivos[0].match(/\d+/)?.[0] || '!', unidad: n.motivos[0].includes('%') ? '%' : 'ms',
      nivel: 'w', to: '/network-health',
      titulo: `Red degradada en ${n.name}`,
      detalle: n.motivos.join(' · '),
      meta: 'enlace o ISP',
    })),
    // Las anomalías vienen consolidadas por (equipo, tipo): una que se repite es una
    // sola línea que dice cuántas veces, no dos titulares iguales.
    ...(anomalies?.anomalies || []).slice(0, 2).map(a => ({
      n: a.occurrences > 1 ? a.occurrences : null,
      unidad: a.occurrences > 1 ? '×' : '',
      nivel: a.severity === 'high' ? 'c' : 'w', to: '/anomalies',
      titulo: a.title,
      detalle: a.occurrences > 1
        ? `${a.hostname} · desde el ${new Date(a.detected_at).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' })}`
        : a.hostname,
      meta: new Date(a.last_detected_at || a.detected_at).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' }),
    })),
  ].filter(Boolean).slice(0, 6);

  // Calificación de seguridad
  const gradeItems = [
    { name: 'Seguro',             color: 'var(--green)', bg: 'var(--green-dim)', value: parseInt(stats?.grade_seguro    || 0) },
    { name: 'Aceptable',          color: 'var(--accent)', bg: 'var(--accent-dim)', value: parseInt(stats?.grade_aceptable || 0) },
    { name: 'Atención requerida', color: 'var(--amber)', bg: 'var(--amber-dim)', value: parseInt(stats?.grade_atencion  || 0) },
    { name: 'Crítico',            color: 'var(--red)', bg: 'var(--red-dim)', value: parseInt(stats?.grade_critico   || 0) },
  ];
  const gradeTotal = gradeItems.reduce((s, g) => s + g.value, 0);
  const gradePieData = gradeItems.filter(g => g.value > 0);

  const renderGradeLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.05) return null;
    const RADIAN = Math.PI / 180;
    const r = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + r * Math.cos(-midAngle * RADIAN);
    const y = cy + r * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central"
        style={{ fontSize: 11, fontWeight: 700 }}>{`${(percent * 100).toFixed(0)}%`}</text>
    );
  };

  // La columna derecha existe solo si hay calificación que mostrar; si no, los
  // titulares se quedan con el ancho completo en vez de dejar media pantalla vacía.
  const hayPaneles = gradeTotal > 0;

  return (
    <div className="dashboard">
      <div className="page-header">
        <div>
          <div className="page-title">Dashboard</div>
          <div className="page-sub">Bienvenido, {user?.full_name} · CMDB actualizada</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={load} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={14} /> Actualizar
          </button>
          <Link to="/assets" className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <ExternalLink size={14} /> Ver inventario
          </Link>
        </div>
      </div>

      <div className="stats-grid">
        <StatCard label="Total activos" value={total} sub="equipos registrados" color="blue" />
        <StatCard label="Reportando" value={reportando} sub="vistos en 24 h" color="blue" />
        <StatCard label="Al día" value={active} sub={`${patchPct}% del total`} color="green" />
        <StatCard label="Pendientes" value={patchPending} sub="requieren actualización" color="orange" />
        <StatCard label="Riesgo de seguridad" value={securityRisk} sub="acción urgente" color="red" />
      </div>

      {securityRisk > 0 && (
        <div className="alert-banner">
          <div className="alert-banner-body">
            <ShieldAlert size={16} color="var(--red)" style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              <div className="alert-banner-title">
                {securityRisk} equipo{securityRisk > 1 ? 's' : ''} con factores de riesgo críticos
              </div>
              <div className="alert-banner-flags">
                {noAntivirus > 0 && <span className="alert-flag"><ShieldOff size={11} /> {noAntivirus} sin antivirus</span>}
                {noFirewall > 0 && <span className="alert-flag"><Wifi size={11} /> {noFirewall} sin firewall</span>}
                {noBitlocker > 0 && <span className="alert-flag"><Lock size={11} /> {noBitlocker} sin BitLocker</span>}
                {localAdmin > 0 && <span className="alert-flag" style={{ color: 'var(--amber)' }}><UserCheck size={11} /> {localAdmin} con admin local</span>}
              </div>
            </div>
          </div>
          <Link to="/security" className="btn btn-secondary btn-sm" style={{ color: 'var(--red)', borderColor: 'var(--red-dim)', flexShrink: 0 }}>
            Ver seguridad
          </Link>
        </div>
      )}

      {/* ── Semáforo de dominio: el veredicto, a un vistazo ── */}
      <div className="semaforo">
        {dominios.map(d => (
          <Link key={d.key} to={d.to} className={`dom dom-${d.nivel}`}>
            <div className="dom-t">{d.label}</div>
            <div className="dom-n" title={compactoConTitulo(d.valor).titulo}>{compactoConTitulo(d.valor).texto}</div>
            <div className="dom-s">{d.sub}</div>
            <div className="dom-d">{d.detalle}</div>
          </Link>
        ))}
      </div>

      {/* Fila principal: los hallazgos a la izquierda ocupando la mitad, y a la
          derecha los dos paneles de contexto apilados. Lo accionable manda; los
          gráficos son el respaldo. */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: (!isMobile && hayPaneles) ? '1fr 1fr' : '1fr',
        gap: 16, alignItems: 'start',
      }}>
        {/* ── Titulares: lo accionable, con nombre y apellido ── */}
        <div className="card titulares">
          <div className="card-header">
            <span className="card-title">Requiere atención</span>
            <span className="tit-meta">{titulares.length === 0 ? 'nada pendiente' : `${titulares.length} hallazgo${titulares.length === 1 ? '' : 's'}`}</span>
          </div>
          {titulares.length === 0 ? (
            <div className="tit-ok">✓ Sin hallazgos abiertos en seguridad, parches ni red</div>
          ) : titulares.map((t, i) => (
            <Link key={i} to={t.to} className={`tit tit-${t.nivel}`}>
              <span className="tit-n" title={t.n != null ? compactoConTitulo(t.n).titulo : undefined}>
                {t.n != null ? compactoConTitulo(t.n).texto : '!'}
                {t.unidad && <span className="tit-u">{t.unidad}</span>}
              </span>
              <span className="tit-cuerpo">
                <span className="tit-t">{t.titulo}</span>
                <span className="tit-s">{t.detalle}</span>
              </span>
              <span className="tit-e">{t.meta}</span>
            </Link>
          ))}
        </div>

        {hayPaneles && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Calificación de seguridad */}
            <div className="card" style={{ padding: 20 }}>
              <div className="card-header" style={{ marginBottom: 16 }}>
                <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <ShieldAlert size={15} style={{ opacity: 0.6 }} /> Calificación de seguridad
                </span>
                <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{gradeTotal} equipo{gradeTotal !== 1 ? 's' : ''} evaluado{gradeTotal !== 1 ? 's' : ''}</span>
              </div>
              {gradeTotal > 0 ? (
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  <ResponsiveContainer width={220} height={220}>
                    <PieChart>
                      <Pie data={gradePieData.map(g => ({ name: g.name, value: g.value }))}
                        cx="50%" cy="50%" innerRadius={52} outerRadius={90} paddingAngle={1}
                        dataKey="value" labelLine={false} label={renderGradeLabel}>
                        {gradePieData.map((g, i) => <Cell key={i} fill={g.color} />)}
                      </Pie>
                      <Tooltip formatter={(v, n) => [`${v} equipo${v !== 1 ? 's' : ''}`, n]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ flex: 1, minWidth: 130 }}>
                    {gradeItems.map((g, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 10px', background: g.bg, borderRadius: 6, marginBottom: 5 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: g.color, flexShrink: 0 }} />
                          <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>{g.name}</span>
                        </div>
                        <span style={{ fontSize: 16, fontWeight: 700, color: g.color }}>{g.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>Sin activos evaluados</div>
              )}
            </div>

          </div>
        )}
      </div>

      {/* ── Panel: Reportes del agente por mes ── */}
      {agentReports.length > 0 && (
        <div className="card" style={{ marginTop: 16, padding: '20px 24px' }}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--ink)' }}>Equipos reportando por día</div>
            <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>Cantidad de equipos únicos que enviaron reporte — últimos 30 días</div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={agentReports} margin={{ top: 8, right: 24, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: 'var(--ink-3)' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11, fill: 'var(--ink-3)' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-subtle)', color: 'var(--ink)', fontSize: 13 }}
                formatter={(v) => [v, 'Reportes']}
              />
              <Line
                type="monotone"
                dataKey="total"
                stroke="#8b8fcf"
                strokeWidth={2}
                dot={{ r: 4, fill: '#8b8fcf', strokeWidth: 0 }}
                activeDot={{ r: 6, fill: 'var(--ink)' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  return <DashboardDesktop />;
}
